import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { SITE_URL, cleanSeoText } from '@/lib/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = SITE_URL;
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;

/**
 * Global fallback strings so pages without a Helmet override never end
 * up with `undefined` / `null` / empty `<title>`. These match the
 * baked-in defaults in `/frontend/public/index.html` so the initial
 * server-delivered HTML and the hydrated SPA agree.
 */
const GLOBAL_DEFAULTS = {
  title: "OGMini — Premium Diecast Collectibles | India's #1 Diecast Store",
  description:
    "India's premier diecast collectibles store. Authentic 1:64 scale models from Mini GT, INNO64, Pop Race, Tarmac Works, Hot Wheels & more. Free shipping across India.",
  og_title: "OGMini — Premium Diecast Collectibles",
  og_description:
    "Authentic 1:64 scale diecast from Mini GT, INNO64, Pop Race & 15+ brands. Curated, original, India-wide shipping.",
  og_image: DEFAULT_OG_IMAGE,
  robots: 'index, follow, max-image-preview:large, max-snippet:-1',
};

// In-memory cache so navigating away and back doesn't re-fetch the
// same page's overrides. Keyed by page_key; value is the resolved
// fields object or `null` for "checked but no custom override".
const CACHE = new Map();
const PENDING = new Map();
const CHECKED = new Map();

/**
 * Fetch admin-configured SEO overrides for a given page key.
 * Returns `{}` when the page has no custom record — the caller merges
 * with `overrides` + `GLOBAL_DEFAULTS` to build the final head.
 * Failures are non-fatal: the frontend falls through to its fallback,
 * so a backend hiccup never blanks a live page's <title>.
 */
async function fetchSeoFields(pageKey) {
  if (!pageKey) return {};
  if (CACHE.has(pageKey) && Date.now() - CHECKED.get(pageKey) < 60000) return CACHE.get(pageKey) || {};
  if (PENDING.has(pageKey)) return PENDING.get(pageKey);
  const request = (async () => { try {
    const { data } = await axios.get(`${API}/seo/page/${encodeURIComponent(pageKey)}`);
    const fields = data?.fields || {};
    CACHE.set(pageKey, fields);
    CHECKED.set(pageKey, Date.now());
    return fields;
  } catch (_) {
    return {};
  } finally { PENDING.delete(pageKey); } })();
  PENDING.set(pageKey, request);
  return request;
}

/**
 * <SEOHead />
 * -----------
 * Central head-tag component. Every storefront page mounts one of
 * these instead of hand-rolling `<Helmet>` blocks so admin overrides,
 * fallbacks, canonical URLs, robots directives and OG tags all flow
 * from one place.
 *
 * Precedence (highest to lowest):
 *   1. Admin-configured override from `site_seo_pages` for `pageKey`
 *   2. Page-supplied `overrides` prop (product name, brand-specific
 *      title, etc.)
 *   3. Global defaults matching /public/index.html
 *
 * Every rendered value is guaranteed to be a non-empty string — no
 * "undefined" / "null" leaks into the DOM, and HTML entities in
 * stored strings (`&#39;`, `&amp;`, `&nbsp;`) are decoded by the
 * backend before they ever reach the client, so Google sees clean
 * text in the snippet.
 *
 * Props:
 *   pageKey   — canonical key (e.g. "home", "shop:brand:INNO64")
 *   overrides — page-generated fallbacks BEFORE the admin override
 *               kicks in. Same shape as GLOBAL_DEFAULTS.
 *   path      — optional path (starts with "/"); used to build a
 *               canonical URL if none is stored.
 *   noindex   — force a `noindex, follow` robots directive
 *               regardless of stored value (used for cart/checkout).
 *   jsonLd    — optional structured-data array to embed under the
 *               <script type="application/ld+json"> tag.
 */
export default function SEOHead({ pageKey, overrides = {}, path, noindex = false, jsonLd = null }) {
  const [record, setRecord] = useState(() => ({ key: pageKey, fields: CACHE.get(pageKey) || {} }));
  const remote = record.key === pageKey ? record.fields : (CACHE.get(pageKey) || {});

  useEffect(() => {
    let cancelled = false;
    fetchSeoFields(pageKey).then((f) => {
      if (!cancelled) setRecord({ key: pageKey, fields: f });
    });
    return () => { cancelled = true; };
  }, [pageKey]);

  const resolved = useMemo(() => {
    // Merge order: defaults → page overrides → admin overrides.
    // Empty strings from any level are ignored so a blank admin field
    // doesn't wipe the page-supplied fallback.
    const pick = (field) => (
      (remote?.[field] && String(remote[field]).trim()) ||
      (overrides?.[field] && String(overrides[field]).trim()) ||
      GLOBAL_DEFAULTS[field] ||
      ''
    );
    // OG title/description gracefully fall back to the meta title/desc
    // when the admin hasn't set social-specific copy — saves duplicate
    // data entry.
    const title = pick('title');
    const description = pick('description');
    const ogTitle = (
      (remote?.og_title && String(remote.og_title).trim()) ||
      (overrides?.og_title && String(overrides.og_title).trim()) ||
      title
    );
    const ogDescription = (
      (remote?.og_description && String(remote.og_description).trim()) ||
      (overrides?.og_description && String(overrides.og_description).trim()) ||
      description
    );
    const ogImage = pick('og_image');
    const robots = noindex
      ? 'noindex, follow'
      : (remote?.robots || overrides?.robots || GLOBAL_DEFAULTS.robots);
    const canonical = (
      (remote?.canonical && String(remote.canonical).trim()) ||
      (overrides?.canonical && String(overrides.canonical).trim()) ||
      (path ? `${SITE}${path}` : '')
    );
    return { title: cleanSeoText(title), description: cleanSeoText(description), ogTitle: cleanSeoText(ogTitle), ogDescription: cleanSeoText(ogDescription), ogImage, robots, canonical, ogType: pick('og_type') || 'website' };
  }, [remote, overrides, path, noindex]);

  return (
    <Helmet>
      <title data-rh="true">{resolved.title}</title>
      <meta data-rh="true" name="description" content={resolved.description} />
      <meta data-rh="true" name="robots" content={resolved.robots} />
      {resolved.canonical && <link data-rh="true" rel="canonical" href={resolved.canonical} />}
      <meta data-rh="true" property="og:title" content={resolved.ogTitle} />
      <meta data-rh="true" property="og:type" content={resolved.ogType} />
      <meta data-rh="true" property="og:description" content={resolved.ogDescription} />
      <meta data-rh="true" property="og:image" content={resolved.ogImage} />
      {resolved.canonical && <meta data-rh="true" property="og:url" content={resolved.canonical} />}
      <meta data-rh="true" name="twitter:card" content="summary_large_image" />
      <meta data-rh="true" name="twitter:title" content={resolved.ogTitle} />
      <meta data-rh="true" name="twitter:description" content={resolved.ogDescription} />
      <meta data-rh="true" name="twitter:image" content={resolved.ogImage} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify((Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map(schema => {
        if (schema['@type'] === 'Product') return { ...schema, offers: { ...schema.offers, url: resolved.canonical } };
        if (schema['@type'] === 'BreadcrumbList') return { ...schema, itemListElement: schema.itemListElement.map((item, i, all) => i === all.length - 1 ? { ...item, item: resolved.canonical } : item) };
        return schema;
      })).replace(/</g, '\\u003c')}</script>}
    </Helmet>
  );
}

/**
 * Small helper for pages that need to invalidate the SEO cache after
 * an admin edit (used inside the AdminSEO panel so the preview updates
 * without a hard reload).
 */
export function invalidateSeoCache(pageKey) {
  if (pageKey) { CACHE.delete(pageKey); CHECKED.delete(pageKey); }
  else { CACHE.clear(); CHECKED.clear(); }
}
